import moment from 'moment';
import { getInitialTypeId } from './getInitialTypeId';

const hasItems = (arr) => Array.isArray(arr) && arr.length > 0;
const createIndexKeys = (arr) => arr.map((_, idx) => idx);

const mapToIds = (items) =>
  Array.isArray(items) ? items.map((item) => item.id) : [];

export const resolveContractorType = (isEditMode, formValues, draftValues) => {
  if (!!draftValues && !isEditMode) {
    return draftValues?.attachOrg?.length > 0 ? 'org_data' : 'fio_data';
  }

  if (!isEditMode) return 'fio_data';

  return formValues?.attachOrg?.length > 0 ? 'org_data' : 'fio_data';
};

const updateKeysBasedOnPriority = (target, source) => {
  if (!source) return;

  if (hasItems(source.attachOrg)) {
    target.orgsKeys = createIndexKeys(source.attachOrg);
  }

  if (hasItems(source.counterpartyOwners)) {
    target.custNameKeys = createIndexKeys(source.counterpartyOwners);
  }

  if (hasItems(source.adresses)) {
    target.addressKeys = createIndexKeys(source.adresses);
  }
};

const createPatchValues = ({ sourceData, baseValues }) => {
  if (!sourceData) return {};

  const patch = {};

  if (hasItems(sourceData.attachOrg) && baseValues.orgsKeys.length) {
    baseValues.orgsKeys.forEach((index) => {
      const org = sourceData.attachOrg[index];
      const orgId = org?.orgId || org?.id;
      if (orgId) {
        patch[`orgId[${index}]`] = orgId;
      }
    });
  }

  if (hasItems(sourceData.adresses) && baseValues.addressKeys.length) {
    baseValues.addressKeys.forEach((index) => {
      const address = sourceData.adresses[index];
      if (address?.streetId) {
        patch[`streetIds[${index}]`] = address.streetId;
      }
      if (address?.houseId) {
        patch[`houseIds[${index}]`] = address.houseId;
      }
      if (address?.addressId) {
        patch[`addressIds[${index}]`] = address.addressId;
      }
    });
  }

  if (
    hasItems(sourceData.counterpartyOwners) &&
    baseValues.custNameKeys.length
  ) {
    baseValues.custNameKeys.forEach((index) => {
      const owner = sourceData.counterpartyOwners[index];
      if (owner?.clientName) {
        patch[`custNames[${index}]`] = owner.clientName;
      }
    });
  }

  return patch;
};

const createBaseValues = ({ mode, formValues, draftValues, isEditMode }) => {
  const sourceData = isEditMode ? formValues : draftValues;

  const baseValues = {
    typeId: getInitialTypeId({
      mode,
      formValues,
      savedDraftValues: draftValues,
    }),
    orgsKeys: [0],
    custNameKeys: [0],
    addressKeys: [0],
    documentVisibility: 2,
    serviceCompanyId: draftValues?.seviceCompanyId || null,
    topicId: draftValues?.topicId || null,
    contractorType: resolveContractorType(isEditMode, formValues, draftValues),
    ResponsibleWorkerId: draftValues?.responsibleWorker?.id || null,
    sourceNumber: draftValues?.sourceNumber ?? null,
    sourceDate: draftValues?.sourceDate ? moment(draftValues.sourceDate) : null,
  };

  updateKeysBasedOnPriority(baseValues, sourceData);

  return baseValues;
};

const createDeferredValues = ({
  formValues,
  isEditMode,
  baseValues,
  draftValues,
}) => {
  return {
    DocNumber: formValues?.docNumber?.split('/')[0],
    docDate: formValues?.docDate ? moment(formValues.docDate) : moment(),
    DeadlineDate: formValues?.deadlineDate
      ? moment(formValues.deadlineDate)
      : undefined,
    doneDate: formValues?.doneDate ? moment(formValues.doneDate) : null,
    topic: formValues?.topic || '',
    statusId: isEditMode ? formValues?.status?.id : undefined,
    ResponsibleWorkerId:
      formValues?.responsibleWorker?.id ?? baseValues.ResponsibleWorkerId,
    executerIds: mapToIds(formValues?.appointedWorkers),
    contractorType: resolveContractorType(isEditMode, formValues, draftValues),
    documentVisibility:
      formValues?.documentVisibility ?? baseValues.documentVisibility,
    subTypeId: formValues?.subtype?.id ?? null,
    serviceCompanyId:
      formValues?.seviceCompanyId ?? baseValues.serviceCompanyId,
    topicId: formValues?.topicId ?? baseValues.topicId,
  };
};

export const getMappedFormValues = ({ mode, formValues, savedDraft }) => {
  const draftValues = savedDraft?.values || {};
  const isEditMode = mode === 'edit';
  const sourceData = isEditMode ? formValues : draftValues;

  const baseValues = createBaseValues({
    mode,
    formValues,
    draftValues,
    isEditMode,
  });

  const patchValues = createPatchValues({ sourceData, baseValues });

  const deferredValues = createDeferredValues({
    formValues,
    isEditMode,
    baseValues,
    draftValues,
  });

  return {
    baseValues,
    deferredValues: { ...deferredValues, ...patchValues },
  };
};
